import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FaPlusCircle,
  FaCube,
  FaShieldAlt,
  FaUserCheck,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaArrowRight,
  FaCheckCircle,
  FaEdit,
  FaTimes,
  FaSpinner,
  FaExclamationTriangle,
} from "react-icons/fa";

// --- Plan Data --------------------------------------------------------------
const medicarePlans = [
  {
    id: "advantage",
    name: "Medicare Advantage Plans",
    description:
      "All‑in‑one coverage including hospital, doctor, and (often) drug benefits with extra perks.",
    icon: <FaPlusCircle className="text-[#e31c3d] text-xl" aria-hidden="true" />,
    learnTo: "/medicare-a",
  },
  {
    id: "partd",
    name: "Part D Drug Plans",
    description: "Stand‑alone prescription coverage to help lower pharmacy costs.",
    icon: <FaCube className="text-[#e31c3d] text-xl" aria-hidden="true" />,
    learnTo: "/medicare-d",
  },
  {
    id: "supplements",
    name: "Medicare Supplements (Medigap)",
    description: "Helps pay Original Medicare copays, coinsurance, and deductibles.",
    icon: <FaShieldAlt className="text-[#e31c3d] text-xl" aria-hidden="true" />,
    learnTo: "/medicare-supplements",
  },
];

const lifePlans = [
  {
    id: "term",
    name: "Term Life Insurance",
    description: "Affordable coverage for 10–30 years; great for income replacement.",
    icon: <FaShieldAlt className="text-[#e31c3d] text-xl" aria-hidden="true" />,
    learnTo: "/term-life",
  },
  {
    id: "permanent",
    name: "Permanent Life Insurance",
    description: "Lifetime protection with potential cash value growth over time.",
    icon: <FaUserCheck className="text-[#e31c3d] text-xl" aria-hidden="true" />,
    learnTo: "/permanent-life",
  },
];

const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5, delay: d },
});

export default function PlansPage() {
  const [userZip, setUserZip] = useState("");
  const [place, setPlace] = useState(null); // { city, state, abbr }
  const [showZipModal, setShowZipModal] = useState(false);
  const [zipDraft, setZipDraft] = useState("");
  const [zipStatus, setZipStatus] = useState("idle"); // idle | checking | valid | invalid
  const [banner, setBanner] = useState("");
  const cacheRef = useRef({});
  const abortRef = useRef(null);
  const navigate = useNavigate();

  // Load ZIP and fetch city/state
  useEffect(() => {
    const zip = localStorage.getItem("userZipCode");
    if (!zip) {
      navigate("/start");
      return;
    }
    setUserZip(zip);
    setZipDraft(zip);
    // resolve location for chip
    void checkZip(zip, { setOnly: true });
  }, [navigate]);

  // Debounced verify when editing in modal
  useEffect(() => {
    setZipStatus("idle");
    if (!/^\d{5}$/.test(zipDraft)) return; // wait until 5 digits
    const t = setTimeout(() => void checkZip(zipDraft), 300);
    return () => clearTimeout(t);
  }, [zipDraft]);

  async function checkZip(codeArg, opts = {}) {
    const code = (codeArg || zipDraft || "").trim();
    if (!/^\d{5}$/.test(code)) return;

    // Use cache if available
    if (cacheRef.current[code]) {
      const info = cacheRef.current[code];
      setPlace(info);
      if (!opts.setOnly) setZipStatus("valid");
      return info;
    }

    // Abort any in-flight request
    try { abortRef.current?.abort(); } catch {}
    abortRef.current = new AbortController();

    if (!opts.setOnly) setZipStatus("checking");

    try {
      const res = await fetch(`https://api.zippopotam.us/us/${code}`, { signal: abortRef.current.signal });
      if (!res.ok) throw new Error("ZIP not found");
      const data = await res.json();
      const first = data?.places?.[0];
      if (!first) throw new Error("ZIP not found");
      const info = {
        city: first["place name"],
        state: first["state"],
        abbr: first["state abbreviation"],
      };
      cacheRef.current[code] = info;
      setPlace(info);
      if (!opts.setOnly) setZipStatus("valid");
      return info;
    } catch (e) {
      if (e?.name === "AbortError") return;
      setPlace(null);
      if (!opts.setOnly) setZipStatus("invalid");
      return null;
    }
  }

  const PlanCard = ({ plan }) => (
    <motion.div
      {...fadeUp(0.05)}
      className="group relative overflow-hidden rounded-2xl border border-[#002b5c]/10 bg-white shadow-sm transition hover:shadow-md"
    >
      {/* Accent bar */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#e31c3d] via-[#ff6b6b] to-[#e31c3d]" />

      <div className="p-6 text-left">
        <div className="flex items-center gap-3">
          {plan.icon}
          <h3 className="text-lg font-bold text-[#002b5c]">{plan.name}</h3>
        </div>
        <p className="mt-2 text-sm text-gray-700">{plan.description}</p>

        {/* Meta bullets */}
        <ul className="mt-3 flex flex-wrap gap-3 text-xs text-gray-600">
          <li className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1">
            <FaCheckCircle aria-hidden className="" /> No‑cost broker help
          </li>
          <li className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1">
            <FaInfoCircle aria-hidden className="" /> Quotes in minutes
          </li>
        </ul>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            onClick={() => navigate(`/questionnaire?plan=${plan.id}`)}
            className="inline-flex items-center rounded-xl bg-[#002b5c] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#001b35] focus:outline-none focus:ring-2 focus:ring-[#002b5c]/30"
          >
            Get started <FaArrowRight className="ml-2" />
          </button>
          {plan.learnTo && (
            <Link
              to={plan.learnTo}
              className="inline-flex items-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-[#002b5c] shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#002b5c]/20"
            >
              Learn more
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="bg-white text-[#002b5c]">
      <Helmet>
        <title>Choose a Plan | Your Choice Associates</title>
        <meta
          name="description"
          content="Browse Medicare and Life Insurance options, then get no‑cost help from a licensed broker near you."
        />
        <link rel="canonical" href="https://insurancebyag.com/plans" />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#f7fbff] to-white">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-16">
          <motion.div {...fadeUp(0)} className="text-center">
            <span className="inline-block rounded-full bg-[#e8f1fb] px-3 py-1 text-xs font-semibold tracking-wider text-[#002b5c]">Plans</span>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">Choose a plan type</h1>
            <p className="mx-auto mt-3 max-w-2xl text-gray-700">
              We’ll match you with a licensed broker near your location to compare plans and enroll together.
            </p>
          </motion.div>

          {/* ZIP / location chip */}
          <motion.div {...fadeUp(0.05)} className="mx-auto mt-6 flex max-w-xl flex-wrap items-center justify-center gap-3 text-sm">
            <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 shadow-sm">
              <FaMapMarkerAlt className="text-[#e31c3d]" aria-hidden />
              ZIP <strong className="ml-1">{userZip}</strong>
            </span>
            {place && (
              <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 shadow-sm">
                <FaCheckCircle className="text-green-600" aria-hidden /> {place.city}, {place.abbr}
              </span>
            )}
            <button
              onClick={() => setShowZipModal(true)}
              className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1.5 font-semibold shadow-sm transition hover:bg-gray-50"
            >
              <FaEdit aria-hidden /> Edit ZIP
            </button>
          </motion.div>

          {banner && (
            <div className="mx-auto mt-4 max-w-xl rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              {banner}
            </div>
          )}
        </div>
      </section>

      {/* Medicare */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-10">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold">Medicare options</h2>
            <Link to="/help/medicare-basics" className="text-sm font-semibold text-[#0B5EFD]">Medicare basics</Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {medicarePlans.map((p) => (
              <PlanCard key={p.id} plan={p} />
            ))}
          </div>
        </div>
      </section>

      {/* Life */}
      <section className="bg-[#f8fafc]">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-10">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold">Life insurance</h2>
            <div className="text-sm text-gray-600">Protect income, debts, and loved ones.</div>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {lifePlans.map((p) => (
              <PlanCard key={p.id} plan={p} />
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-12">
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                title: "Tell us what you need",
                text: "Answer a few questions so we can route you to the best local broker.",
              },
              {
                title: "Compare plans side‑by‑side",
                text: "Your broker checks networks, prescriptions, and costs across carriers.",
              },
              {
                title: "Enroll with confidence",
                text: "Get help completing enrollment and support year‑round—at no extra cost.",
              },
            ].map((s, i) => (
              <motion.div
                key={i}
                {...fadeUp(0.05 * (i + 1))}
                className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
              >
                <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold">
                  {i + 1}
                </div>
                <h3 className="mt-3 text-base font-bold text-[#002b5c]">{s.title}</h3>
                <p className="mt-1 text-sm text-gray-700">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* ZIP Modal */}
      {showZipModal && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowZipModal(false)} />
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative z-10 w-full max-w-md rounded-2xl bg-white shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
              <h4 className="text-base font-semibold">Update ZIP code</h4>
              <button className="rounded-md p-2 hover:bg-gray-100" onClick={() => setShowZipModal(false)} aria-label="Close">
                <FaTimes />
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const clean = (zipDraft || "").trim();
                if (!/^\d{5}$/.test(clean)) return setBanner("Please enter a valid 5‑digit ZIP.");
                if (zipStatus !== "valid") {
                  const ok = await checkZip(clean);
                  if (!ok) return;
                }
                localStorage.setItem("userZipCode", clean);
                setUserZip(clean);
                setShowZipModal(false);
                setBanner("ZIP updated. Showing brokers near " + clean + ".");
              }}
              className="px-5 py-4"
            >
              <label htmlFor="zip" className="text-sm font-medium">
                ZIP code
              </label>
              <div className={`mt-1 flex items-center rounded-xl border ${zipStatus === "invalid" ? "border-red-300" : "border-gray-300"} bg-white pl-3 focus-within:ring-2 focus-within:ring-[#0B5EFD]/30`}>
                <FaMapMarkerAlt className="text-[#e31c3d]" aria-hidden />
                <input
                  id="zip"
                  value={zipDraft}
                  onChange={(e) => {
                    const v = (e.target.value || '').replace(/\D/g, '').slice(0, 5);
                    setZipDraft(v);
                  }}
                  className="w-full rounded-xl border-0 bg-transparent px-3 py-2 outline-none"
                  placeholder="e.g. 11201"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  aria-describedby="zip-status"
                  maxLength={5}
                />
                <div className="pr-3">
                  {zipStatus === "checking" && <FaSpinner className="animate-spin" aria-label="Checking" />}
                  {zipStatus === "valid" && <FaCheckCircle className="text-green-600" aria-label="Valid ZIP" />}
                  {zipStatus === "invalid" && <FaExclamationTriangle className="text-red-500" aria-label="Invalid ZIP" />}
                </div>
              </div>
              <div id="zip-status" className="mt-2 min-h-[1.25rem] text-sm" aria-live="polite">
                {zipStatus === "checking" && <span className="text-gray-600">Checking ZIP…</span>}
                {zipStatus === "valid" && place && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1 text-green-800">
                    <FaCheckCircle /> {place.city}, {place.abbr}
                  </span>
                )}
                {zipStatus === "invalid" && <span className="text-red-700">ZIP not found. Please check and try again.</span>}
              </div>

              <div className="mt-4 flex items-center justify-end gap-2">
                <button type="button" onClick={() => setShowZipModal(false)} className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold shadow-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!/^\d{5}$/.test(zipDraft) || zipStatus === "checking" || zipStatus === "invalid"}
                  className="rounded-xl bg-[#002b5c] px-4 py-2 text-sm font-semibold text-white shadow-sm transition enabled:hover:bg-[#001b35] disabled:opacity-60"
                >
                  Save ZIP
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
